/**
 * useFileSend — the outbound file-transfer state machine, lifted out of
 * `SendFileCard` so more than one presentation can drive it.
 *
 * `SendFileCard` still owns the picker + device select and renders the machine
 * as a standalone card. `FileTransferList` needs the same machine, but as one
 * row alongside incoming transfers — and a component cannot hand its internal
 * state to a sibling. Hence the hook.
 *
 * Behaviour is unchanged from the card's original implementation; this is an
 * extraction, not a rewrite. The one addition is `sendNow(file)`, which the
 * list uses because the redesign has no separate Send button: choosing a file
 * (or dropping one) starts the transfer immediately, as the Figma shows.
 */
import { useCallback, useState } from "react";
import type { FileSendCapabilities, FileSendProgressEvent } from "./FileSendCapabilities";

export type SendCardState =
  | { status: "idle" }
  | { status: "too-large" }
  | { status: "preparing"; pct: number | null }
  | { status: "delivering"; transferId?: string }
  /** `needsAppOpen` — the recipient can't be woken, so the offer waits for the app to be opened. */
  | { status: "waiting-accept"; transferId?: string; needsAppOpen?: boolean }
  | {
      status: "uploading";
      partIndex: number;
      partCount: number;
      transferId?: string;
      transport?: "p2p";
    }
  | { status: "done" }
  | { status: "failed"; reason: string; errorCode?: string };

export interface UseFileSendResult {
  state: SendCardState;
  /** Chunks the peer has acknowledged, when the peer reports them. */
  peerChunksDone: number | null;
  /** True while a transfer is in flight — the picker must stay disabled. */
  busy: boolean;
  /** Start a transfer for an already-picked file. */
  send: (file: File, deviceId: string) => void;
  cancel: () => void;
  reset: () => void;
  /** Set the machine to `too-large` without starting anything. */
  rejectTooLarge: () => void;
  transferId: string | undefined;
}

export function useFileSend(capabilities: FileSendCapabilities): UseFileSendResult {
  const [state, setState] = useState<SendCardState>({ status: "idle" });
  const [peerChunksDone, setPeerChunksDone] = useState<number | null>(null);

  const reset = useCallback(() => {
    setState({ status: "idle" });
    setPeerChunksDone(null);
  }, []);

  const rejectTooLarge = useCallback(() => {
    setState({ status: "too-large" });
    setPeerChunksDone(null);
  }, []);

  /**
   * ONE event reducer shared by send / resume / cancel: a resumed upload runs
   * behind `capabilities.resume` rather than the original `send` call, so it
   * needs the same machine wired to ITS progress events.
   */
  const applyEvent = useCallback((event: FileSendProgressEvent) => {
    switch (event.stage) {
      case "hashing": {
        // Large files gate the offer on this pass, so show its progress rather
        // than dead air. Bytes are optional — null pct means indeterminate.
        const pct =
          event.totalBytes === undefined
            ? null
            : event.totalBytes > 0
              ? Math.floor(((event.bytesHashed ?? 0) / event.totalBytes) * 100)
              : 100;
        setState({ status: "preparing", pct });
        break;
      }
      case "delivering":
        setState({ status: "delivering", transferId: event.transferId });
        break;
      case "waiting-accept":
        setState({
          status: "waiting-accept",
          transferId: event.transferId,
          needsAppOpen: event.needsAppOpen,
        });
        break;
      case "uploading":
        setState({
          status: "uploading",
          partIndex: event.partIndex,
          partCount: event.partCount,
          transferId: event.transferId,
          transport: event.transport,
        });
        break;
      case "done":
        setState({ status: "done" });
        break;
      case "failed":
        setState({ status: "failed", reason: event.reason, errorCode: event.errorCode });
        break;
      case "peer-progress":
        setPeerChunksDone(event.chunksDone);
        break;
      default:
        break;
    }
  }, []);

  const send = useCallback(
    (file: File, deviceId: string) => {
      if (!deviceId || file.size > capabilities.maxBytes) return;
      // Optimistic first state is `delivering`: sendFile's first event is
      // `delivering`, promoted to `waiting-accept` by the recipient's ack.
      setState({ status: "delivering" });
      setPeerChunksDone(null);
      void capabilities.send(file, deviceId, applyEvent).catch(() => {
        // Every rejection path in sendFile reports a terminal `failed` stage
        // through onProgress before throwing — nothing further to do here.
      });
    },
    [capabilities, applyEvent]
  );

  const transferId =
    state.status === "delivering" ||
    state.status === "waiting-accept" ||
    state.status === "uploading"
      ? state.transferId
      : undefined;

  const cancel = useCallback(() => {
    if (transferId) void capabilities.cancel?.(transferId, applyEvent);
  }, [transferId, capabilities, applyEvent]);

  /**
   * `preparing` MUST be in this union. `send` optimistically sets
   * `delivering`, but the first `hashing` event downgrades to `preparing` — so
   * omitting it re-enables the picker mid-hash, and a second pick starts an
   * entire second transfer (new transferId, second offer to the peer). Every
   * non-terminal in-flight status belongs here; terminal ones do not.
   */
  const busy =
    state.status === "preparing" ||
    state.status === "delivering" ||
    state.status === "waiting-accept" ||
    state.status === "uploading";

  return { state, peerChunksDone, busy, send, cancel, reset, rejectTooLarge, transferId };
}
